import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  VStack,
  Tooltip
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faMinus,
  faPlus
} from '@fortawesome/pro-regular-svg-icons';
import { ButtonIcon, ButtonText } from '@morressier/alembic';
import {
  WorkflowBucket,
  WorkflowSubBucket
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';

interface WorkFlowAccordionProps {
  journalId: string;
  workFlowGroups: WorkflowBucket[];
  setWorkflowState: (state: {
    filters: Record<string, any>;
    subBucket: string;
    subBucketId: string;
    bucketId: string;
    isDynamic: boolean;
  }) => void;
}

const WorkFlowAccordion: React.FC<WorkFlowAccordionProps> = ({
  journalId,
  workFlowGroups,
  setWorkflowState
}) => {
  const [expandedIndices, setExpandedIndices] = useState<number[]>([0]);

  const getSubBucketId = (group: WorkflowBucket, sub: WorkflowSubBucket) => {
    if ('subBucketId' in sub) return sub.subBucketId;
    return `${group.bucketId}-${sub.subBucketName}`;
  };

  const handleSubBucketSelect = (
    group: WorkflowBucket,
    sub: WorkflowSubBucket
  ) => {
    if (group.isDynamic) {
      if (!('filterValue' in sub)) return;

      setWorkflowState({
        filters: {
          [group.filterName]: [sub.filterValue],
          journalId: [journalId]
        },
        subBucket: sub.subBucketName,
        subBucketId: getSubBucketId(group, sub),
        bucketId: group.bucketId,
        isDynamic: true
      });
      return;
    }

    setWorkflowState({
      filters: 'filter' in sub ? sub.filter : {},
      subBucket: sub.subBucketName,
      subBucketId: getSubBucketId(group, sub),
      bucketId: group.bucketId,
      isDynamic: false
    });
  };

  return (
    <Box width={{ base: '100%', xl: '50%', '2xl': '50%' }}>
      <Accordion
        allowMultiple
        index={expandedIndices}
        onChange={indices =>
          setExpandedIndices(Array.isArray(indices) ? indices : [indices])
        }
      >
        {workFlowGroups.map(group => {
          return (
            <AccordionItem key={group.bucketId} mb="4">
              {({ isExpanded }) => (
                <>
                  <AccordionButton background="actionSecondaryFill">
                    <Flex
                      flex="1"
                      textAlign="left"
                      justify="space-between"
                      align="center"
                    >
                      <Text textStyle="headingItem">{group.bucketName}</Text>
                      <Box ml={2}>
                        <FontAwesomeIcon
                          icon={isExpanded ? faMinus : faPlus}
                          color="blue"
                        />
                      </Box>
                    </Flex>
                  </AccordionButton>

                  <AccordionPanel p={3} borderRadius="md" bg="white">
                    <VStack align="stretch" spacing={1}>
                      {group.subBuckets.map(sub => (
                        <Flex
                          key={getSubBucketId(group, sub)}
                          align="center"
                          gap={2}
                        >
                          <ButtonText
                            disabled={sub.count === 0}
                            justifyContent="flex-start"
                            whiteSpace="normal"
                            textAlign="left"
                            onClick={() => handleSubBucketSelect(group, sub)}
                          >
                            {sub.subBucketName} ({sub.count})
                          </ButtonText>
                          {sub.definition && (
                            <Tooltip
                              label={sub.definition || ''}
                              hasArrow
                              placement="auto"
                            >
                              <Box as="span" cursor="pointer">
                                <ButtonIcon
                                  size="small"
                                  ariaLabel="Journal bucket info icon"
                                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                                  marginBottom="2px"
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Flex>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          );
        })}
      </Accordion>
    </Box>
  );
};

export default WorkFlowAccordion;
